import { FC, useEffect, useState } from 'react';
import CloseIcon from '../../assets/images/shopping/closeIcon';
import NodiIcon from '../../assets/images/shopping/nodiIcon';
import { IMinDetails, IShipmentData, IproductReferenceDataInCountry } from '../../types/orders';
import NudosDatePicker from '../NudosComponents/NudosDatePicker';
import { NudosButton } from '../NudosComponents';
import ProductSelector from '../../views/orders/Components/ProductSelector/ProductSelector';
import DoubleConfirmationModal from '../DoubleConfirmationModal/DoubleConfirmationModal';
import { finishDelivery, massUpdateInProgressToDelivery } from '../../services/traking.services';
import { displayErrorNotification, displaySuccessNotification } from '../../utils/displayNudosStandardNotifications';
import { IWarehouseTemporaryStorageModalCustomSectionInitialValues } from '../../views/orders/OrderDetailInternalCard/Components/Tab4Address/Components/ShipmentCard/WarehouseTemporaryStorageModal/WarehouseTemporaryStorageModalCustomSection';
import { updateStockOrderShipmentStatus } from '../../services/stockOrders';
import { LOCATION_PLACE_TYPE } from 'types/services';
import { extractProductData } from 'utils/extractProductData';
import './DeliveryOrderModal.scss';

interface IDeliveryModal {
  listOfGroupedShipments: IShipmentData[];
  dataTraking: IShipmentData;
  closeModal: () => void;
  updateDeliveryOrder?: () => void;
  loadingUpdate?: boolean;
  orderId: string;
  organizationName?: string;
  receiverName?: string;
  flag?: string | null;
  setShowConfirmDeliveryModal?: (value: boolean) => void;
  warehouseTemporaryStorage?: IWarehouseTemporaryStorageModalCustomSectionInitialValues;
  warehouseDestination?: boolean;
  isStockOrder?: boolean;
  references?: IproductReferenceDataInCountry[];
}

const DeliveryOrderModal: FC<IDeliveryModal> = ({
  dataTraking,
  closeModal,
  updateDeliveryOrder,
  loadingUpdate,
  orderId,
  organizationName = 'Nombre Empresa',
  receiverName = 'Nombre Cliente',
  flag = null,
  listOfGroupedShipments,
  warehouseTemporaryStorage,
  warehouseDestination,
  isStockOrder = false,
  references
}: IDeliveryModal) => {
  const [dateInfo, setDateInfo] = useState<string>();
  const [loadingUpdateList, setLoadingUpdateList] = useState<boolean>(false);
  const [errorList, setErrorList] = useState<IMinDetails[]>();
  const [showConfirmDeliveryModal, setShowConfirmDeliveryModal] = useState<boolean>(false);
  const [listOfShipmentsToChangeStatus, setListOfShipmentsToChangeStatus] = useState<IShipmentData[]>([]);
  const [hasProductSelectorErrors, setHasProductSelectorErrors] = useState<boolean>(false);

  const hasTemporaryStorage = !!warehouseTemporaryStorage?.estimatedEntryDate;
  const stateOfTheSelectedItem = dataTraking?.statusId;
  const shipmentIdSelectedItem = dataTraking?.shipmentId;
  const shipmentsAvailableToChangeStatus = isStockOrder
    ? listOfGroupedShipments?.filter(
        shipments => shipments?.statusId === stateOfTheSelectedItem && shipments?.shipmentId !== shipmentIdSelectedItem
      )
    : listOfGroupedShipments?.filter(
        shipments =>
          shipments.statusId === stateOfTheSelectedItem &&
          shipments.shipmentId !== shipmentIdSelectedItem &&
          !!shipments?.warehouseTemporaryStorage?.estimatedEntryDate === hasTemporaryStorage
      );

  const defaultFlag =
    'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/Flag_of_Mexico.svg/2560px-Flag_of_Mexico.svg.png';

  const countryTimeZone = dataTraking?.country?.timeZone || undefined;

  const isButtonDisabled =
    !dateInfo ||
    loadingUpdateList ||
    loadingUpdate ||
    hasProductSelectorErrors ||
    !listOfShipmentsToChangeStatus?.length;

  const extractShipmentIds = (shippingList: IShipmentData[]) => {
    return shippingList.map(shipment => ({
      shipmentId: shipment.shipmentId,
      deliveryDate: dateInfo || null,
      newSerials: extractProductData(shipment?.products)
    }));
  };

  const alertSavingWithoutSerials = () => {
    displaySuccessNotification({
      customJSXMessage: <> Estas guardando sin seriales, recuerda que luego los debes agregar en lista de productos</>
    });
  };

  const stockOrderBodyData = {
    stockOrderId: Number(orderId),
    stockShipmentStatusId: 3,
    deliveredInfo: {
      deliveryDate: dateInfo ?? '',
      serialsInfo: listOfShipmentsToChangeStatus?.flatMap(
        shipment =>
          shipment?.products?.map(product => {
            return {
              stockShipmentId: shipment?.shipmentId,
              productId: product?.id,
              serial: product?.serial || ''
            };
          }) || []
      )
    }
  };

  const handleDuplicatedSerialError = (updateData: IMinDetails[]) => {
    if (!Array.isArray(updateData)) return;
    const existErrors = updateData?.filter(element => element.error);
    if (!existErrors?.length) return;
    setErrorList(existErrors);
    setShowConfirmDeliveryModal(false);
    return existErrors;
  };

  const saveData = async () => {
    if (isButtonDisabled) return;
    setLoadingUpdateList(true);
    try {
      const bodyData = extractShipmentIds(listOfShipmentsToChangeStatus);

      const updateData: IMinDetails[] = isStockOrder
        ? await updateStockOrderShipmentStatus(stockOrderBodyData)
        : await massUpdateInProgressToDelivery(bodyData);

      const allHaveSerial = listOfShipmentsToChangeStatus.every(shipment =>
        shipment.products.every(product => Boolean(!!product?.serial))
      );

      const existErrors = handleDuplicatedSerialError(updateData);
      if (existErrors) return;

      await finishDelivery(orderId);
      await updateDeliveryOrder?.();
      closeModal();
      if (!allHaveSerial) alertSavingWithoutSerials();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingUpdateList(false);
    }
  };

  const defaultPlace = (place: string) => {
    if (place === LOCATION_PLACE_TYPE.USER) return dataTraking?.receiverInfo?.receiverName;
    if (place === LOCATION_PLACE_TYPE.OFFICE) return `Oficinas ${dataTraking?.receiverInfo?.receiverName}`;
    if (place === LOCATION_PLACE_TYPE.NUDOS) return `Bodegas ${dataTraking?.country?.name}`;
    return '';
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTraking]);

  return (
    <>
      {showConfirmDeliveryModal && (
        <DoubleConfirmationModal
          handleClose={() => setShowConfirmDeliveryModal(false)}
          handleConfirmationText={saveData}
          loadingConfirmation={loadingUpdateList}
          verificationText={`Cambiar a entregado Envio ${defaultPlace(dataTraking?.place || '')}`}
          companyNameInfo={{
            orderId: Number(orderId),
            companyName: organizationName,
            countryFlag: flag || '',
            destination: receiverName
          }}
        />
      )}
      <div id="newModalDeliveryOrder" style={{ display: showConfirmDeliveryModal ? 'none' : '' }}>
        <div className="containerForm">
          <div className="floatClose" onClick={() => closeModal?.()}>
            <CloseIcon />
          </div>
          <div className="boxForm">
            <div className="titleModal">
              <div className="containerIconNodi">
                <NodiIcon />
              </div>
              Entrega de productos
            </div>
            <div className="containerSubTitle">
              <span className="firstPartSubTitle">{`${organizationName} - Orden  N°${orderId ?? 1}`}</span>
              <span>|</span>
              <span>{`Envío - ${receiverName}`}</span>
              <img src={flag ?? defaultFlag} width={14} alt="delivery-order-flag" />
            </div>
            <div className="containerDetails">
              <div className="containerDate">
                <NudosDatePicker
                  componentSize="small"
                  label="Fecha de entrega (obligatorio)"
                  defaultValue={dateInfo}
                  updateCurrentValueCallback={setDateInfo}
                  isFilled={false}
                  customLabel="customMyLabel"
                  desiredTimeZone={countryTimeZone}
                />
              </div>
              <div className="containerProducts">
                <ProductSelector
                  references={references}
                  headerTitle="Selecciona las herramientas que pasan a entregadas"
                  shippingChosenByDefault={dataTraking}
                  listOfShipmentsAvailableToAdd={shipmentsAvailableToChangeStatus}
                  setListOfShipmentsToChangeStatus={setListOfShipmentsToChangeStatus}
                  errorList={errorList}
                  setErrorList={setErrorList}
                  serialHasAnyError={hasErrors => setHasProductSelectorErrors(hasErrors)}
                  requireSerials
                  isStockOrder={isStockOrder}
                  customAlertMessage={
                    <>
                      Para cambiar el <strong>estado a Entregado</strong>, es obligatorio ingresar los seriales. Así, el
                      equipo de bodega podrá registrarlo correctamente.
                    </>
                  }
                />
              </div>
            </div>
            <div className="buttonContainer">
              <NudosButton
                componentSize="large"
                buttonText="Guardar"
                handleClick={() => setShowConfirmDeliveryModal?.(true)}
                isButtonDisabled={isButtonDisabled}
                isButtonLoading={loadingUpdateList || loadingUpdate}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeliveryOrderModal;
